/**
 * useClienteModals - Hook for managing cliente page modals
 *
 * Handles: All modal visibility states
 */
import { useState } from 'react';
import type { Cliente } from '../services/clienteService';
import type { ProductoCliente, ProductoClienteGTIN } from '../types/produtoCliente';
import type { Representante } from '../services/representantesService';

export interface UseClienteModalsReturn {
  // Cliente modal
  showClienteModal: boolean;
  setShowClienteModal: (show: boolean) => void;
  editingCliente: Cliente | null;
  setEditingCliente: (cliente: Cliente | null) => void;

  // Produto modal
  showProdutoModal: boolean;
  setShowProdutoModal: (show: boolean) => void;
  editingProduto: ProductoCliente | null;
  setEditingProduto: (produto: ProductoCliente | null) => void;

  // GTIN modal
  showGTINModal: boolean;
  setShowGTINModal: (show: boolean) => void;
  selectedProduto: ProductoCliente | null;
  setSelectedProduto: (produto: ProductoCliente | null) => void;
  produtoGTINs: ProductoClienteGTIN[];
  setProdutoGTINs: (gtins: ProductoClienteGTIN[]) => void;

  // Representante modal
  showRepresentanteModal: boolean;
  setShowRepresentanteModal: (show: boolean) => void;
  editingRepresentante: Representante | null;
  setEditingRepresentante: (representante: Representante | null) => void;

  // Vincular hospitais modal
  showVincularHospitaisModal: boolean;
  setShowVincularHospitaisModal: (show: boolean) => void;

  // Helpers
  openCreateCliente: () => void;
  openEditCliente: (cliente: Cliente) => void;
  openCreateProduto: () => void;
  openEditProduto: (produto: ProductoCliente) => void;
  openCreateRepresentante: () => void;
  openEditRepresentante: (representante: Representante) => void;
  closeGTINModal: () => void;
}

export const useClienteModals = (): UseClienteModalsReturn => {
  // Cliente modal
  const [showClienteModal, setShowClienteModal] = useState(false);
  const [editingCliente, setEditingCliente] = useState<Cliente | null>(null);

  // Produto modal
  const [showProdutoModal, setShowProdutoModal] = useState(false);
  const [editingProduto, setEditingProduto] = useState<ProductoCliente | null>(null);

  // GTIN modal
  const [showGTINModal, setShowGTINModal] = useState(false);
  const [selectedProduto, setSelectedProduto] = useState<ProductoCliente | null>(null);
  const [produtoGTINs, setProdutoGTINs] = useState<ProductoClienteGTIN[]>([]);

  // Representante modal
  const [showRepresentanteModal, setShowRepresentanteModal] = useState(false);
  const [editingRepresentante, setEditingRepresentante] = useState<Representante | null>(null);

  // Vincular hospitais modal
  const [showVincularHospitaisModal, setShowVincularHospitaisModal] = useState(false);

  // Helper functions
  const openCreateCliente = () => {
    setEditingCliente(null);
    setShowClienteModal(true);
  };

  const openEditCliente = (cliente: Cliente) => {
    setEditingCliente(cliente);
    setShowClienteModal(true);
  };

  const openCreateProduto = () => {
    setEditingProduto(null);
    setShowProdutoModal(true);
  };

  const openEditProduto = (produto: ProductoCliente) => {
    setEditingProduto(produto);
    setShowProdutoModal(true);
  };

  const openCreateRepresentante = () => {
    setEditingRepresentante(null);
    setShowRepresentanteModal(true);
  };

  const openEditRepresentante = (representante: Representante) => {
    setEditingRepresentante(representante);
    setShowRepresentanteModal(true);
  };

  const closeGTINModal = () => {
    setShowGTINModal(false);
    setSelectedProduto(null);
    setProdutoGTINs([]);
  };

  return {
    // Cliente modal
    showClienteModal,
    setShowClienteModal,
    editingCliente,
    setEditingCliente,

    // Produto modal
    showProdutoModal,
    setShowProdutoModal,
    editingProduto,
    setEditingProduto,

    // GTIN modal
    showGTINModal,
    setShowGTINModal,
    selectedProduto,
    setSelectedProduto,
    produtoGTINs,
    setProdutoGTINs,

    // Representante modal
    showRepresentanteModal,
    setShowRepresentanteModal,
    editingRepresentante,
    setEditingRepresentante,

    // Vincular hospitais modal
    showVincularHospitaisModal,
    setShowVincularHospitaisModal,

    // Helpers
    openCreateCliente,
    openEditCliente,
    openCreateProduto,
    openEditProduto,
    openCreateRepresentante,
    openEditRepresentante,
    closeGTINModal,
  };
};
